import type {
    CoreSchemaConfig,
    RefInfo,
    ResolvedReference,
    SchemaFile,
    SchemasCollection,
    WixReferenceAnalysis
} from '@wix/document-services-types'
import _ from 'lodash'
import draftSchema from './draftSchema.json'
import {CannotFindSchemaError, SchemaValidationError, SchemaValidationErrorDetails} from './errors'
import {combineAllOf, dereference} from './schemaUtils'
import {buildVariableReferencePropertiesForSingleSchema} from '../../common/variables/variableReferencePropertiesBuilder'
import {buildVariableConnectionsForSchema} from '../../common/variables/variableConnectionsBuilder'
import * as variablesManifest from '../namespaces/variables/variablesManifest.json'
import * as referenceUtil from './referenceUtil'
import {createInstance, ErrorObject, Ajv8Type} from '../utils/ajv'
import {removeUnevaluated} from '../utils/removeUnevaluated'
import {removeDeprecatedProperties} from './deprecatedProperties'

export type ValidationFunction = (namespace: string, dataTypeName: string, data: any) => void

export interface SchemaServiceOptions {
    enforceStrictValidationEverywhere: boolean
    skipBuilderSchemaValidation: boolean
}

const convertAjvError = (error: ErrorObject): SchemaValidationErrorDetails => {
    return _.pick(error, ['message', 'instancePath', 'keyword', 'schemaPath', 'params'])
}

const convertAjvErrors = (errors: ErrorObject[]): SchemaValidationError =>
    new SchemaValidationError(_.map(errors, convertAjvError))

interface CoreValidators {
    withDefaults: Ajv8Type
    validation: Ajv8Type
    removing?: Ajv8Type
    strict?: Ajv8Type
}

const keyByPath = (refInfos: RefInfo[]) => _.keyBy(refInfos, info => info.path.join('.'))

const requiredReferencesFields: (keyof RefInfo)[] = [
    'jsonPointer',
    'isList',
    'referencedMap',
    'shouldCollect',
    'shouldValidate',
    'refTypes',
    'path'
]
const optionalReferencesFields: (keyof RefInfo)[] = [
    'isRefOwner',
    'isRelationalSplit',
    'dontSerialize',
    'variableTypesByPattern'
]
const allowedReferencesFields = new Set([...requiredReferencesFields, ...optionalReferencesFields])

const createSchemaCore = (
    coreSchemaConfig: CoreSchemaConfig,
    referencesMap: WixReferenceAnalysis,
    schemaServiceOptions?: SchemaServiceOptions
) => {
    const {namespaces, permanentDataTypes, formats, restrictedNonInflatedSchemas} = coreSchemaConfig
    const ajvOptions = {
        strict: false,
        useDefaults: false,
        allowUnionTypes: true,
        strictNumbers: true
    }
    const validators = {
        withDefaults: createInstance({...ajvOptions, useDefaults: true}, formats),
        validation: createInstance(ajvOptions, formats)
    } as CoreValidators

    let nsInRegistration: Set<string>
    let isRegistering = false
    const startRegisteringSchemas = () => {
        isRegistering = true
        nsInRegistration = new Set<string>()
    }

    const finishRegisteringSchemas = () => {
        isRegistering = false
        for (const nsName of nsInRegistration) {
            removeSchema(namespaces[nsName].$id)
            addInitialSchema(namespaces[nsName], namespaces[nsName].$id, nsName)
        }
        nsInRegistration = new Set<string>()
    }

    const batchRegisterSchemas = (f: Function) => {
        if (isRegistering) {
            //we could write code to support this, but it's to prevent developer errors until this mechanism is improved:
            throw new Error('cannot register a batch inside of a batch')
        }
        startRegisteringSchemas()
        f()
        finishRegisteringSchemas()
    }

    if (restrictedNonInflatedSchemas) {
        validators.removing = createInstance(
            {
                ...ajvOptions,
                removeAdditional: 'failing',
                allErrors: true,
                discriminator: true
            },
            formats
        )
        validators.strict = createInstance(ajvOptions, formats)
    }

    const cachedInflatedSchemas = new Map<string, any>()

    function addInflatedSchemaToCache(namespace: string, dataType: string, schema: any) {
        const cacheKey = getValidationKey(namespace, dataType)
        cachedInflatedSchemas.set(cacheKey, schema)
    }

    function getInflatedSchemaFromCache(namespace: string, dataType: string) {
        const cacheKey = getValidationKey(namespace, dataType)
        return cachedInflatedSchemas.get(cacheKey)
    }

    function deleteInflatedSchemaFromCache(cacheKey: string) {
        cachedInflatedSchemas.delete(cacheKey)
    }

    function registerSchema(schema: SchemaFile, id: string) {
        _.forOwn(validators, validator => {
            validator!.addSchema(schema, id)
        })
    }

    function addInitialSchema(schema: SchemaFile, id: string, namespace: string) {
        const strictSchemaToUse = restrictedNonInflatedSchemas![namespace]
        _.forOwn(validators, (validator, validatorName) => {
            const schemaToRegister =
                (['removing', 'strict'].includes(validatorName) ? strictSchemaToUse : schema) ?? schema
            validator!.addSchema(schemaToRegister, id)
        })
    }

    _.forOwn(namespaces, (schema: SchemaFile, name: string) => {
        addInitialSchema(schema, schema.$id ?? name, name)
    })

    function removeSchema(id: string) {
        _.forOwn(validators, validator => {
            validator!.removeSchema(id)
        })
        deleteInflatedSchemaFromCache(id)
    }

    function getValidationKey(namespace: string, dataTypeName: string): string {
        const ns = namespaces[namespace]
        return `${ns.$id}#/${dataTypeName}`
    }

    function validateWithCustomValidator(
        validatorsToUse: Ajv8Type[],
        namespace: string,
        dataTypeName: string,
        data: any,
        context: Record<string, any>
    ): void {
        if (!(namespace in namespaces)) {
            throw new CannotFindSchemaError(namespace, dataTypeName)
        }
        removeDeprecatedProperties(namespace, data)
        const key = getValidationKey(namespace, dataTypeName)
        const validationErrors: any = _.reduce<Ajv8Type, any[]>(
            validatorsToUse,
            (errors: any[], validator) => {
                const validationFunction = validator.getSchema(key)
                const isValid = validationFunction?.(data)
                if (!isValid) {
                    if (context.validator === 'removeAdditionalProperties') {
                        removeUnevaluated(data, validationFunction?.errors as any[])
                        const isValidAfterRemoval = validationFunction?.(data)
                        if (!isValidAfterRemoval) {
                            errors.push(...(validationFunction?.errors ?? []))
                        }
                    } else {
                        errors.push(...(validationFunction?.errors ?? []))
                    }
                }
                return errors
            },
            []
        )
        if (validationErrors?.length > 0) {
            throwValidationErrors(validationErrors)
        }
    }

    // mutate
    const addDefaultsAndValidate: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        validateWithCustomValidator(
            schemaServiceOptions?.enforceStrictValidationEverywhere && validators.strict
                ? [validators.withDefaults, validators.strict]
                : [validators.withDefaults],
            namespace,
            dataTypeName,
            data,
            {
                validator: 'addDefaultsAndValidate'
            }
        )
    }

    const validateStrict: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        const dataToValidate = _.cloneDeep(data)
        validateWithCustomValidator(
            [validators.strict ?? validators.withDefaults],
            namespace,
            dataTypeName,
            dataToValidate,
            {
                validator: 'validateStrict'
            }
        )
    }

    const validateNoDefaults: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        validateWithCustomValidator([validators.validation], namespace, dataTypeName, data, {
            validator: 'validateNoDefaults'
        })
    }

    const validateReferences = (refs: unknown): refs is RefInfo[] => {
        if (!Array.isArray(refs)) {
            throw new Error('Expected an array')
        }

        refs.forEach((ref, index) => {
            if (typeof ref !== 'object' || ref === null) {
                throw new Error(`Item at index ${index} is not an object`)
            }
            const keys = Object.keys(ref)
            for (const field of requiredReferencesFields) {
                if (!(field in ref)) {
                    throw new Error(`Missing required field "${field}" at index ${index}`)
                }
            }
            for (const key of keys) {
                if (!allowedReferencesFields.has(key as keyof RefInfo)) {
                    throw new Error(`Unexpected field "${key}" at index ${index}`)
                }
            }
        })

        return true
    }

    function removeAdditionalProperties(namespace: string, dataTypeName: string, data: any): void {
        if (validators.removing) {
            validateWithCustomValidator([validators.removing], namespace, dataTypeName, data, {
                validator: 'removeAdditionalProperties'
            })
        }
    }

    function throwValidationErrors(errors?: ErrorObject[] | null) {
        throw convertAjvErrors(errors ?? [])
    }

    function hasNamespace(namespace: string) {
        return !!_.get(namespaces, [namespace])
    }

    function hasSchemaForDataType(namespace: string, dataTypeName: string) {
        return !!_.get(namespaces, [namespace, dataTypeName])
    }

    // This is a workaround to remove internal cache created by AJV fragments behaviour
    // We get to this flow only in "Schema Dev Mode"
    // See - https://github.com/ajv-validator/ajv/issues/1293
    function clearAjvFragmentCacheForSchema(namespace: string, schema: SchemaFile, schemaName: string) {
        const key = getValidationKey(namespace, schemaName)
        removeSchema(`#/${schemaName}`)
        removeSchema(key)
        registerSchema(schema, key)
        registerSchema(schema, `#/${schemaName}`)
    }

    function extendDraftSchema(schema: any) {
        if (schema.isDraftSchema) {
            if (schema.allOf) {
                return {
                    isDraftSchema: true,
                    ...schema,
                    allOf: [draftSchema, ...schema.allOf]
                }
            }

            if (schema.properties) {
                return {
                    ..._.omit(schema, ['properties']),
                    isDraftSchema: true,
                    properties: _.merge(schema.properties, draftSchema.properties),
                    required: _.union(schema.required || [], draftSchema.required)
                }
            }
            return {
                ...schema,
                isDraftSchema: true,
                properties: draftSchema.properties,
                required: _.union(schema.required || [], draftSchema.required)
            }
        }
        return schema
    }

    const getVariableOptions = (key: string) => {
        let [nsName, schemaName] = key.split('#/')
        if (!schemaName) {
            schemaName = nsName
            nsName = 'commonSchemas'
        }
        const shortSchemaName = schemaName.split('/').pop() ?? ''
        const nsNameInVariablesManifest = nsName.replace('.json', '').replace('urn:dm:schemas:', '')
        return (variablesManifest.variables as any)[nsNameInVariablesManifest]?.[shortSchemaName]
    }

    const extendVariableConnectionsMapByRefValue = (map: Record<string, string[]>) =>
        Object.entries(map).reduce((extendedVariableConnectionsMap, [variableName, paths]) => {
            const refTypesValues = referencesMap.variables[variableName].value.refTypes
            const variableRefTypes = refTypesValues.flatMap(
                refTypeValue => referencesMap.variables[refTypeValue]['variableConnections.variableId'].refTypes
            )
            variableRefTypes.forEach(refType => {
                extendedVariableConnectionsMap[refType] ??= []
                extendedVariableConnectionsMap[refType].push(...paths)
            })
            return extendedVariableConnectionsMap
        }, {})

    const extendWithVariables = (schema: any, restrictAtRoot: boolean) => {
        const variableConnectionsMap = buildVariableReferencePropertiesForSingleSchema({}, schema, getVariableOptions)
        if (_.isEmpty(variableConnectionsMap)) {
            return schema
        }
        const extendedVariableConnectionsMap = extendVariableConnectionsMapByRefValue(variableConnectionsMap)
        const variableConnectionsSchema = buildVariableConnectionsForSchema(extendedVariableConnectionsMap)
        if (restrictAtRoot) {
            if (_.has(schema, ['additionalProperties'])) {
                delete schema.additionalProperties
            }
            if (_.has(schema, ['unevaluatedProperties'])) {
                delete schema.unevaluatedProperties
            }
        }
        return {
            type: 'object',
            allOf: [schema, variableConnectionsSchema],
            ...(restrictAtRoot ? {unevaluatedProperties: false} : {})
        }
    }

    function extendSchemaAndCombineAllOf(
        ns: any,
        schema: any,
        name: string,
        namespace: string,
        shouldCombineAllOfs: boolean = true
    ) {
        const extended = extendDraftSchema(extendWithVariables(schema, !shouldCombineAllOfs))
        const dereferenced = dereference(extended, {ns, common: namespaces.common, name})
        return shouldCombineAllOfs ? combineAllOf(dereferenced) : dereferenced
    }

    function inflateSchema(namespace: string, schema: any, name: string) {
        const dereferenced = dereference(schema, {ns: namespaces[namespace], common: namespaces.common, name})
        return combineAllOf(dereferenced)
    }

    const REFERENCE_TYPES = ['ref', 'weakRef', 'refList']

    function isReference(val: any) {
        return _.intersection(REFERENCE_TYPES, val?.pseudoType).length > 0
    }

    function extractRefsFromSchema(namespace: string, schema: any, result: RefInfo[], parentPath: string[]) {
        const subSchemas = schema.allOf || schema.anyOf || schema.oneOf || []
        _.forEach(subSchemas, subSchema => extractRefsFromSchema(namespace, subSchema, result, parentPath))
        if (schema.properties) {
            const {$ref} = schema.properties
            const currentSchema = $ref ? namespaces[namespace][$ref] : schema
            _.forOwn(currentSchema.properties, (val, key) => {
                if (val?.$ref) {
                    val = namespaces[namespace][val.$ref]
                    if (!val) {
                        return
                    }
                }
                if (isReference(val)) {
                    const path = parentPath.concat(key)
                    const isWeakRef = val?.pseudoType.includes('weakRef')
                    const isList = val?.pseudoType.includes('refList')
                    result.push({
                        path,
                        isList,
                        jsonPointer: `/${path.join('/')}`,
                        shouldCollect: val.shouldCollect ?? !isWeakRef,
                        isRefOwner: val.shouldCollect ?? !isWeakRef,
                        shouldValidate: val.shouldValidate ?? !isWeakRef,
                        referencedMap: val.referencedMap ?? namespace,
                        isRelationalSplit: val.isRelationalSplit,
                        refTypes: val.refTypes ?? [],
                        ...(val.variableTypesByPattern?.items && {
                            variableTypesByPattern: {
                                items: val.variableTypesByPattern.items
                            }
                        })
                    })
                } else if (val.type === 'object' && val.properties) {
                    extractRefsFromSchema(namespace, val, result, parentPath.concat(key))
                } else if (val.type === 'array' && val.items) {
                    extractRefsFromSchema(namespace, val.items, result, parentPath.concat(key))
                }
            })
        }
    }

    const combineReferences = (refs: RefInfo[]): RefInfo[] => {
        if (refs.length > 1) {
            const groups = _.groupBy(refs, 'jsonPointer')
            return _.map(groups, group => {
                if (group.length === 1) {
                    return group[0]
                }

                const reference = {...group[0]}
                reference.refTypes = _.flatMap(group, 'refTypes')

                if (group.some(g => g.variableTypesByPattern)) {
                    const variableTypesByPatternItems = _.compact(_.flatMap(group, 'variableTypesByPattern.items'))
                    reference.variableTypesByPattern = {
                        items: variableTypesByPatternItems
                    }
                }

                return reference
            })
        }
        return refs
    }

    function extractAndAddReferences(namespace: string, name: string) {
        const ns = namespaces[namespace]
        const schema = ns[name]
        const refs: RefInfo[] = []
        extractRefsFromSchema(namespace, schema, refs, [])
        if (refs.length) {
            const combined = combineReferences(refs)
            addReferences(namespace, name, combined)
        }
    }

    function addReferences(namespace: string, name: string, refs: RefInfo[]) {
        try {
            validateReferences(refs)
        } catch (error) {
            throw new Error(`References validation failed: ${(error as Error).message}`)
        }
        _.setWith(referencesMap, [namespace, name], keyByPath(refs), Object)
    }

    function addSchemaCollection(schemasCollection: SchemasCollection) {
        Object.keys(schemasCollection).forEach(namespace => {
            if (!hasNamespace(namespace)) {
                throw new Error(
                    `Namespace ${namespace} does not exist. Creating new namespaces via addSchemaCollection is not supported.`
                )
            }
            const ns = namespaces[namespace]
            const id = ns.$id
            //TODO: now that we are on AJV8, check if we still need to remove whole NS and then add again
            // it is possible that we can just add the new schemas, and this will save a ton on compile time of schemas during load
            // const areNewSchemasIntroduced = _.some(schemasCollection[namespace] as any, (v, k) => ns[k])

            if (!isRegistering) {
                removeSchema(id)
            }

            _.forOwn(schemasCollection[namespace], (schema, schemaName) => {
                // TODO: find a better solution for this. Can remove this and see what fails in the build.
                // This is mainly for test flows, as we do not support "override" of existing schemas anymore
                // but between tests in same file, if we use same schema name but different schema, without this the tests will fail
                const key = getValidationKey(namespace, schemaName)
                removeSchema(`#/${schemaName}`)
                removeSchema(key)
            })

            const schemasToRegister = _.mapValues(schemasCollection[namespace], schema => extendDraftSchema(schema))
            _.assign(ns, schemasToRegister)
            if (restrictedNonInflatedSchemas) {
                _.assign(restrictedNonInflatedSchemas[namespace], schemasToRegister)
            }
            if (isRegistering) {
                nsInRegistration.add(namespace)
            }
            if (!isRegistering) {
                addInitialSchema(ns, id, namespace)
            }
        })
    }

    function addDataTypesToExistingNamespace(
        namespace: string,
        schemas: Record<string, SchemaFile>,
        override: boolean = false
    ) {
        const ns = namespaces[namespace]
        const id = ns.$id
        removeSchema(id)
        _.forEach(schemas, (schema, name) => {
            if (restrictedNonInflatedSchemas) {
                const extended = extendSchemaAndCombineAllOf(ns, schema, name, namespace, false)
                ns[name] = extended
                restrictedNonInflatedSchemas[namespace][name] = extended
            }
            if (override) {
                clearAjvFragmentCacheForSchema(namespace, ns[name] as unknown as SchemaFile, name)
            }
            extractAndAddReferences(namespace, name)
        })
        addInitialSchema(ns, id, namespace)
    }

    function registerNamespace(name: string, schemas: SchemaFile) {
        if (!namespaces[name]) {
            const ns = schemas.$id ? schemas : {...schemas, $id: name}
            namespaces[name] = ns
            addInitialSchema(ns, ns.$id, name)
        }
    }

    const isDraftDataSchema = (namespace: string, dataTypeName: string) =>
        _.get(namespaces, [namespace, dataTypeName, 'isDraftSchema'], false)
    const isDraftItem = (dataItem: any) => !!dataItem?._isDraftItem

    function extractReferences(namespace: string, dataTypeName: string, data: any): readonly ResolvedReference[] {
        return referenceUtil.extractReferences(referencesMap, namespace, dataTypeName, data)
    }

    //this function extracts the fields from the data item which are 'reference' fields according to the schema
    function extractReferenceFieldsInfo(namespace: string, dataTypeName: string, data?: any): readonly RefInfo[] {
        const refs = _.get(referencesMap, [namespace, dataTypeName])
        if (data) {
            return _.filter(refs, (refInfo: RefInfo) => _.has(data, refInfo.path))
        }
        return _.values(refs)
    }

    function extractOwnedReferenceFieldsInfo(
        namespace: string,
        dataTypeName: string,
        onlySerializable?: boolean,
        data?: any
    ): Record<string, RefInfo> {
        const refs = _.get(referencesMap, [namespace, dataTypeName])
        return _.pickBy(
            refs,
            refInfo =>
                refInfo.isRefOwner &&
                (!data || _.has(data, refInfo.path)) &&
                (!onlySerializable || !refInfo.dontSerialize)
        )
    }

    const isPermanentDataType = (namespace: string, type: string): boolean =>
        _.get(permanentDataTypes, [namespace, type], false)

    return {
        batchRegisterSchemas,
        addDefaultsAndValidate,
        validate: validateNoDefaults,
        validateStrict,
        validateNoDefaults,
        hasNamespace,
        hasSchemaForDataType,
        isPermanentDataType,
        registerNamespace,
        addSchemaCollection,
        addDataTypesToExistingNamespace,
        isDraftDataSchema,
        isDraftItem,
        extractReferenceFieldsInfo,
        extractOwnedReferenceFieldsInfo,
        extractReferences,
        addReferences,
        removeAdditionalProperties,
        options: Object.freeze(schemaServiceOptions),
        inflateSchema,
        getInflatedSchemaFromCache,
        addInflatedSchemaToCache
    }
}

export {createSchemaCore}
